import * as Sentry from '@sentry/remix';
import { type BasicNode, type TRootElement, truncateAfterContentGate } from '@shared/dream-components';
import structuredClone from '@ungap/structured-clone';

import type { NodeContent, Page } from '~/interfaces/page';
import type { Subscription } from '~/interfaces/subscriptions';
import { findMultipleTipTapContentNodes } from '~/utils/findTipTapContentNode';
import getCookieValue from '~/utils/getCookieValue';
import { safeJsonParse } from '~/utils/safeJsonParse';
import { shouldClipAfterContentGate } from '~/utils/shouldClipAfterContentGate';

interface ProcessContentGateParams {
  page: Page;
  requestUrl: string;
  cookies: string;
}

interface ContentGateResult {
  isContentClipped: boolean;
  clippedPageContent: TRootElement | BasicNode[] | null;
  subscription?: Subscription;
}

/**
 * Processes content gate logic for a post page
 *
 * Determines if content should be clipped based on:
 * - Content gate node presence
 * - User subscription status
 * - Tier access permissions
 *
 * @param params - Page data, request URL, and cookies
 * @returns Object containing clipping status, clipped content, and subscription
 */
export async function processContentGate(params: ProcessContentGateParams): Promise<ContentGateResult> {
  const { page, requestUrl, cookies } = params;

  const defaultResult: ContentGateResult = {
    isContentClipped: false,
    clippedPageContent: null,
    subscription: undefined,
  };

  try {
    const content = structuredClone(page.viewable_page_version?.content) as NodeContent | null | undefined;
    if (!content) {
      return defaultResult;
    }

    const url = new URL(requestUrl);
    const searchParams = new URLSearchParams(url.search);

    // Check if page has content gate
    const foundNodes = findMultipleTipTapContentNodes(content, ['contentGate']);
    if (!foundNodes.contentGate) {
      return defaultResult;
    }

    // Fetch subscription if user has token
    let subscription: Subscription | undefined;
    const token = getCookieValue('token', cookies);
    if (token) {
      const requestBody: { jwt_token: string; subscriber_id?: string } = { jwt_token: token };
      const subscriptionRes = await fetch('/current_subscription', {
        method: 'POST',
        headers: {
          'Sec-Fetch-Dest': 'empty',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (subscriptionRes.ok) {
        subscription = await safeJsonParse<Subscription>(subscriptionRes);
      }
    }

    // Determine if content should be clipped
    const isContentClipped = shouldClipAfterContentGate({
      contentGateNode: foundNodes.contentGate,
      subscription,
      cookiesHeader: cookies,
      currentTierId: searchParams.get('current_tier_id'),
    });

    // Truncate content if needed
    const clippedPageContent = isContentClipped ? truncateAfterContentGate(content as TRootElement) : null;

    return {
      isContentClipped,
      clippedPageContent,
      subscription,
    };
  } catch (error) {
    Sentry.captureException(error);
    return defaultResult;
  }
}
